/**
 * ExecutionStatusBadge - Display execution status with appropriate styling
 *
 * Shows a colored badge with icon indicating the status of a connector execution.
 * The status visual mapping lives in @/lib/executionStatus.
 */
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { STATUS_CONFIGS, resolveStatusConfig } from '@/lib/executionStatus';
import type { ExecutionStatus } from '@/types/connectors';

export interface ExecutionStatusBadgeProps {
  status: ExecutionStatus;
  className?: string;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CONFIGS = {
  sm: {
    badge: 'text-xs px-2 py-0.5',
    icon: 'h-3 w-3',
  },
  md: {
    badge: 'text-sm px-2.5 py-1',
    icon: 'h-3.5 w-3.5',
  },
  lg: {
    badge: 'text-base px-3 py-1.5',
    icon: 'h-4 w-4',
  },
};

/**
 * ExecutionStatusBadge component
 *
 * @example
 * <ExecutionStatusBadge status="success" />
 * <ExecutionStatusBadge status="running" showIcon={false} />
 * <ExecutionStatusBadge status="failed" size="lg" />
 */
export function ExecutionStatusBadge({
  status,
  className,
  showIcon = true,
  size = 'md',
}: ExecutionStatusBadgeProps) {
  const config = resolveStatusConfig(status);
  // For dev-mode warnings, check directly so we keep the existing diagnostic.
  if (import.meta.env.DEV && status && !(STATUS_CONFIGS as any)[status]) {
    console.warn(
      `[ExecutionStatusBadge] Unknown status received: "${status}". Falling back to "pending". ` +
      `Valid statuses are: ${Object.keys(STATUS_CONFIGS).join(', ')}`
    );
  }

  const sizeConfig = SIZE_CONFIGS[size];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn(
        'inline-flex items-center gap-1.5 font-medium',
        config.colorClasses,
        config.bgClasses,
        sizeConfig.badge,
        // Add pulse animation for running status
        status === 'running' && 'animate-pulse',
        className
      )}
      role="status"
      aria-label={`Execution ${config.label}`}
    >
      {showIcon && (
        <Icon
          className={cn(
            sizeConfig.icon,
            config.iconAnimation
          )}
          aria-hidden="true"
        />
      )}
      <span>{config.label}</span>
    </Badge>
  );
}
